/**
 * FilePatternsStep - Wizard step 3 for file pattern configuration
 *
 * Collects:
 * - Include patterns (required, at least one)
 * - Exclude patterns (optional)
 * - Recursive scanning flag
 */

import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Plus, X, AlertCircle, Info } from 'lucide-react';
import { useMonitorCreationStore } from '@/features/monitors/store/monitorCreationStore';

/**
 * Common file pattern examples
 */
const PATTERN_EXAMPLES = {
  include: [
    '*.parquet',
    '*.csv',
    '*.json',
    'data_*.parquet',
    '*.txt',
  ],
  exclude: [
    '*_temp.*',
    '*_backup.*',
    '.*',
    '*_old.*',
    '*_archive.*',
  ],
};

/**
 * FilePatternsStep Component
 *
 * Third step of monitor creation wizard for file pattern configuration.
 *
 * @example
 * ```tsx
 * <FilePatternsStep />
 * ```
 */
export const FilePatternsStep: React.FC = () => {
  const store = useMonitorCreationStore();

  const [includeInput, setIncludeInput] = useState('');
  const [excludeInput, setExcludeInput] = useState('');

  /**
   * Add include pattern
   */
  const handleAddInclude = () => {
    if (includeInput.trim()) {
      store.addIncludePattern(includeInput.trim());
      setIncludeInput('');
    }
  };

  /**
   * Add exclude pattern
   */
  const handleAddExclude = () => {
    if (excludeInput.trim()) {
      store.addExcludePattern(excludeInput.trim());
      setExcludeInput('');
    }
  };

  /**
   * Quick add example pattern
   */
  const handleQuickAddInclude = (pattern: string) => {
    if (!store.file_patterns.include_patterns.includes(pattern)) {
      store.addIncludePattern(pattern);
    }
  };

  const handleQuickAddExclude = (pattern: string) => {
    if (!store.file_patterns.exclude_patterns.includes(pattern)) {
      store.addExcludePattern(pattern);
    }
  };

  return (
    <div className="space-y-6">
      {/* Include Patterns Card */}
      <Card>
        <CardHeader>
          <CardTitle>Include Patterns</CardTitle>
          <CardDescription>
            Specify glob patterns for files to monitor (at least one required)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Pattern Input */}
          <div className="flex gap-2">
            <Input
              value={includeInput}
              onChange={(e) => setIncludeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddInclude()}
              placeholder="e.g., *.parquet"
              className="flex-1 font-mono"
            />
            <Button onClick={handleAddInclude} disabled={!includeInput.trim()}>
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>

          {/* Pattern List */}
          {store.file_patterns.include_patterns.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {store.file_patterns.include_patterns.map((pattern, index) => (
                <Badge key={index} variant="default" className="text-sm px-3 py-1">
                  <span className="font-mono">{pattern}</span>
                  <button
                    onClick={() => store.removeIncludePattern(index)}
                    className="ml-2 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No include patterns added yet. Add at least one pattern to continue.
              </AlertDescription>
            </Alert>
          )}

          {/* Quick Add Examples */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Common patterns:</p>
            <div className="flex flex-wrap gap-2">
              {PATTERN_EXAMPLES.include.map((pattern) => (
                <Button
                  key={pattern}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAddInclude(pattern)}
                  disabled={store.file_patterns.include_patterns.includes(pattern)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  <span className="font-mono text-xs">{pattern}</span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exclude Patterns Card */}
      <Card>
        <CardHeader>
          <CardTitle>Exclude Patterns (Optional)</CardTitle>
          <CardDescription>
            Specify glob patterns for files to ignore
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Pattern Input */}
          <div className="flex gap-2">
            <Input
              value={excludeInput}
              onChange={(e) => setExcludeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddExclude()}
              placeholder="e.g., *_temp.*"
              className="flex-1 font-mono"
            />
            <Button onClick={handleAddExclude} disabled={!excludeInput.trim()}>
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>

          {/* Pattern List */}
          {store.file_patterns.exclude_patterns.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {store.file_patterns.exclude_patterns.map((pattern, index) => (
                <Badge key={index} variant="outline" className="text-sm px-3 py-1">
                  <span className="font-mono">{pattern}</span>
                  <button
                    onClick={() => store.removeExcludePattern(index)}
                    className="ml-2 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Quick Add Examples */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Common exclusions:</p>
            <div className="flex flex-wrap gap-2">
              {PATTERN_EXAMPLES.exclude.map((pattern) => (
                <Button
                  key={pattern}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAddExclude(pattern)}
                  disabled={store.file_patterns.exclude_patterns.includes(pattern)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  <span className="font-mono text-xs">{pattern}</span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recursive Option */}
      <Card>
        <CardHeader>
          <CardTitle>Scan Options</CardTitle>
          <CardDescription>
            Configure how the directory should be scanned
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="recursive"
              checked={store.file_patterns.recursive}
              onCheckedChange={(checked) => store.setRecursive(checked as boolean)}
            />
            <Label
              htmlFor="recursive"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Scan subdirectories recursively
            </Label>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            When enabled, the monitor will scan all subdirectories within the specified directory path
          </p>
        </CardContent>
      </Card>

      {/* Pattern Syntax Help */}
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertTitle>Pattern Syntax Guide</AlertTitle>
        <AlertDescription>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div>
              <p className="font-semibold mb-1">Wildcards:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><code className="font-mono bg-info/10 px-1 rounded">*</code> - Matches any characters</li>
                <li><code className="font-mono bg-info/10 px-1 rounded">?</code> - Matches single character</li>
                <li><code className="font-mono bg-info/10 px-1 rounded">[abc]</code> - Matches any character in brackets</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-1">Examples:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><code className="font-mono bg-info/10 px-1 rounded">*.csv</code> - All CSV files</li>
                <li><code className="font-mono bg-info/10 px-1 rounded">data_*.parquet</code> - Parquet files starting with "data_"</li>
                <li><code className="font-mono bg-info/10 px-1 rounded">file_?.txt</code> - file_1.txt, file_a.txt, etc.</li>
              </ul>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};
